// Comms email pass (1.12.212-beta): the formatted email body and the extra email recipients (Cc / Bcc).
//
// Plain JS with no DOM, so the browser (composer + bubble), Jest and the Deno Edge Functions all use the SAME rules:
//   sanitizeEmailHtml   — keep only the formatting the composer can make (bold, italic, underline, lists, links, line breaks).
//                         Every kept tag is rebuilt from scratch with only safe attributes; scripts, styles, event handlers
//                         (onclick=…), javascript: links and everything else are dropped. Run on send AND on display.
//   emailHtmlToText     — the plain-text alternative sent with the HTML (and what SMS/WhatsApp get when switching back).
//   plainTextToEmailHtml / appendSignatureHtml — a typed body or a signature as safe HTML.
//   parseEmailAddressList / emailAddressListError — the Cc / Bcc fields: many addresses, comma / semicolon / space separated.

const ALLOWED_TAGS = new Set(['p', 'div', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'span', 'blockquote']);
// Tags whose whole content goes, not just the tag.
const DROP_WITH_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template', 'svg', 'math', 'textarea', 'title', 'head', 'select', 'button', 'form'];
const TAG_ALIAS = { b: 'strong', i: 'em' };
const VOID_TAGS = new Set(['br']);

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0', '#39': "'" };
function decodeEntities(value) {
    return String(value ?? '').replace(/&(#x[0-9a-f]+|#\d+|[a-z]+|#39);/gi, (match, name) => {
        const key = name.toLowerCase();
        if (key.startsWith('#x')) {
            const code = Number.parseInt(key.slice(2), 16);
            return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
        }
        if (key.startsWith('#')) {
            const code = Number.parseInt(key.slice(1), 10);
            return Number.isFinite(code) && code > 0 && code < 0x110000 ? String.fromCodePoint(code) : '';
        }
        return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key) ? NAMED_ENTITIES[key] : match;
    });
}

/** Only http(s) and mailto links survive; anything else (javascript:, data:, vbscript:, relative) is dropped. */
export function safeLinkHref(raw) {
    // Browsers ignore control characters and whitespace inside a scheme ("java\tscript:"), so strip them before checking.
    // eslint-disable-next-line no-control-regex
    const decoded = decodeEntities(String(raw ?? '')).replace(/[\u0000-\u0020\u007f-\u009f]/g, '');
    if (!decoded) return '';
    if (/^https?:\/\//i.test(decoded) || /^mailto:[^\s@]+@[^\s@]+$/i.test(decoded)) return decoded;
    return '';
}

function readHref(attributes) {
    const match = /(?:^|\s)href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i.exec(attributes || '');
    if (!match) return '';
    return safeLinkHref(match[1] ?? match[2] ?? match[3] ?? '');
}

/**
 * The formatted email body, made safe. Unknown tags are removed but their text stays; text is re-escaped, so nothing that
 * looks like markup can survive outside the rebuilt allow-listed tags. Unclosed tags are closed at the end.
 */
// Review fix round 1: anything past 200 KB is cut off first, with a note.
// Review fix round 2: the cap alone did not bound the time (204 KB of unclosed "<a " took ~26 s, because each failed tag was
// re-scanned to the end of the text). The body is now read in ONE forward pass: every character is looked at a fixed number
// of times, and a tag that never closes (an open quote, or no ">" before the end) ends the markup — the rest is shown as
// plain, escaped text, never re-scanned. (Browsers drop such a broken tail entirely; showing it as text loses nothing safe.)
export const EMAIL_HTML_MAX_CHARS = 200 * 1024;
export const EMAIL_HTML_TRUNCATED_NOTE = '[… the rest of this message was too long to show here]';
// Review fix round 2: what staff-send-reply accepts as a formatted body (the composer's own output is far smaller).
export const STAFF_REPLY_HTML_MAX_CHARS = 50 * 1024;

const DROP_WITH_CONTENT_SET = new Set(DROP_WITH_CONTENT);
const isSpace = (ch) => ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
const isAsciiLetter = (ch) => (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z');
const isNameChar = (ch) => isAsciiLetter(ch) || (ch >= '0' && ch <= '9') || ch === '-';

/**
 * Where "</name …>" (any case, spaces before ">") next ends, at or after `from`, in the lower-cased text; -1 when never.
 * Each call only moves forward, and the caller continues from the answer, so the drop-with-content search stays linear.
 */
function findClosingTag(lower, name, from) {
    const needle = `</${name}`;
    let at = lower.indexOf(needle, from);
    while (at !== -1) {
        let j = at + needle.length;
        while (j < lower.length && isSpace(lower[j])) j += 1;
        if (lower[j] === '>') return j + 1;
        at = lower.indexOf(needle, at + 1);
    }
    return -1;
}

export function sanitizeEmailHtml(input) {
    let html = String(input ?? '');
    if (!html.trim()) return '';
    let truncated = false;
    if (html.length > EMAIL_HTML_MAX_CHARS) {
        html = html.slice(0, EMAIL_HTML_MAX_CHARS);
        // Don't leave half a tag at the cut.
        const lastOpen = html.lastIndexOf('<');
        if (lastOpen > html.lastIndexOf('>')) html = html.slice(0, lastOpen);
        truncated = true;
    }
    const lower = html.toLowerCase();
    const n = html.length;
    const out = [];
    const open = [];
    // How many of each tag are on the open stack. A closing tag with nothing open is ignored in O(1) — before this, every
    // unmatched "</u>" re-scanned the whole open stack (lastIndexOf), so "<p>"×N + "</u>"×N was quadratic. A matched
    // close still searches back, but only as far as the tags it then pops, and each tag is popped once: linear overall.
    const openCounts = new Map();
    const pushOpen = (tag) => {
        open.push(tag);
        openCounts.set(tag, (openCounts.get(tag) || 0) + 1);
    };
    const popOpen = () => {
        const tag = open.pop();
        openCounts.set(tag, openCounts.get(tag) - 1);
        return tag;
    };
    const pushText = (text) => {
        if (text) out.push(escapeHtml(decodeEntities(text)));
    };
    const emitTag = (closing, raw, attributes) => {
        if (!ALLOWED_TAGS.has(raw)) return;
        const tag = TAG_ALIAS[raw] || raw;
        if (VOID_TAGS.has(tag)) {
            if (!closing) out.push('<br>');
            return;
        }
        if (closing) {
            if (!openCounts.get(tag)) return;
            const at = open.lastIndexOf(tag);
            while (open.length > at) out.push(`</${popOpen()}>`);
            return;
        }
        if (tag === 'a') {
            const href = readHref(attributes);
            if (!href) {
                // A link with no safe target keeps its words but is not a link.
                pushOpen('span');
                out.push('<span>');
                return;
            }
            pushOpen('a');
            out.push(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`);
            return;
        }
        pushOpen(tag);
        out.push(`<${tag}>`);
    };

    // Text runs from textFrom; it is only flushed when markup is actually consumed, so a run of stray "<" is one text push.
    let pos = 0;
    let textFrom = 0;
    while (pos < n) {
        const lt = html.indexOf('<', pos);
        if (lt === -1) break;
        // Comments, CDATA, <!DOCTYPE …>, <?xml …?>: removed (an unclosed one removes the rest).
        if (lower.startsWith('<!--', lt)) {
            const end = html.indexOf('-->', lt + 4);
            pushText(html.slice(textFrom, lt));
            pos = end === -1 ? n : end + 3;
            textFrom = pos;
            continue;
        }
        if (lower.startsWith('<![cdata[', lt)) {
            const end = html.indexOf(']]>', lt + 9);
            pushText(html.slice(textFrom, lt));
            pos = end === -1 ? n : end + 3;
            textFrom = pos;
            continue;
        }
        if (html[lt + 1] === '!') {
            const end = html.indexOf('>', lt + 2);
            if (end === -1) break; // never closes: the rest is text
            pushText(html.slice(textFrom, lt));
            pos = end + 1;
            textFrom = pos;
            continue;
        }
        if (html[lt + 1] === '?') {
            const end = html.indexOf('?>', lt + 2);
            pushText(html.slice(textFrom, lt));
            pos = end === -1 ? n : end + 2;
            textFrom = pos;
            continue;
        }
        // A tag: "<", spaces, optional "/", spaces, a name that starts with a letter.
        let j = lt + 1;
        while (j < n && isSpace(html[j])) j += 1;
        let closing = false;
        if (html[j] === '/') {
            closing = true;
            j += 1;
            while (j < n && isSpace(html[j])) j += 1;
        }
        if (!isAsciiLetter(html[j] || '')) {
            // Not a tag ("<3", "5 < 6"): the "<" stays part of the text run.
            pos = lt + 1;
            continue;
        }
        const nameStart = j;
        while (j < n && isNameChar(html[j])) j += 1;
        const raw = lower.slice(nameStart, j);
        if (!closing && DROP_WITH_CONTENT_SET.has(raw)) {
            // <script>, <style>, … : the tag and everything up to its closing tag go (unclosed = the rest goes).
            const end = findClosingTag(lower, raw, j);
            pushText(html.slice(textFrom, lt));
            pos = end === -1 ? n : end;
            textFrom = pos;
            continue;
        }
        // The attributes, up to the ">" that ends the tag. Quoted values may hold ">" (href="data:…<b>…"), so they are
        // skipped whole. An open quote or no ">" = the tag never ends: the rest is plain text (one pass, no re-scan).
        const attrStart = j;
        let tagEnd = -1;
        while (j < n) {
            const ch = html[j];
            if (ch === '>') { tagEnd = j; break; }
            if (ch === '"' || ch === "'") {
                const close = html.indexOf(ch, j + 1);
                if (close === -1) break;
                j = close + 1;
                continue;
            }
            j += 1;
        }
        if (tagEnd === -1) break; // never closes: the rest is text
        pushText(html.slice(textFrom, lt));
        if (!(closing && DROP_WITH_CONTENT_SET.has(raw))) emitTag(closing, raw, html.slice(attrStart, tagEnd));
        pos = tagEnd + 1;
        textFrom = pos;
    }
    if (textFrom < n) pushText(html.slice(textFrom));
    while (open.length) out.push(`</${popOpen()}>`);
    if (truncated) out.push(`<p>${escapeHtml(EMAIL_HTML_TRUNCATED_NOTE)}</p>`);
    return out.join('').replace(/<span><\/span>/g, '');
}

/** True when the HTML carries formatting that plain text would lose (bold, a list, a link…), not just paragraphs and breaks. */
export function emailHtmlHasFormatting(html) {
    return /<(strong|b|em|i|u|ul|ol|li|a|blockquote)\b/i.test(sanitizeEmailHtml(html));
}

/** The plain-text alternative: paragraphs and breaks become new lines, list items get "• " or "1. ", links keep their address. */
export function emailHtmlToText(input) {
    const html = sanitizeEmailHtml(input);
    if (!html) return '';
    const lists = [];
    let text = html.replace(/<(\/?)(ul|ol|li|a|p|div|br|blockquote)([^>]*)>/g, (_tag, closing, name, attrs) => {
        if (name === 'br') return '\n';
        if (name === 'ul' || name === 'ol') {
            if (closing) { lists.pop(); return '\n'; }
            lists.push({ kind: name, count: 0 });
            return '\n';
        }
        if (name === 'li') {
            if (closing) return '\n';
            const list = lists[lists.length - 1];
            const indent = '  '.repeat(Math.max(0, lists.length - 1));
            if (!list || list.kind === 'ul') return `${indent}• `;
            list.count += 1;
            return `${indent}${list.count}. `;
        }
        if (name === 'a') {
            if (closing) return '\u0001';
            const href = /href="([^"]*)"/.exec(attrs)?.[1] || '';
            return `\u0002${href}\u0003`;
        }
        // p, div, blockquote
        return closing ? '\n' : '\n';
    });
    // "words (https://…)" — unless the words already are the address.
    // eslint-disable-next-line no-control-regex
    text = text.replace(/\u0002([^\u0003]*)\u0003([^\u0001]*)\u0001/g, (_m, href, words) => {
        const target = decodeEntities(href);
        const label = decodeEntities(words.replace(/<[^>]+>/g, ''));
        const shown = target.replace(/^mailto:/i, '');
        return !label.trim() || label.trim() === shown ? shown : `${label} (${shown})`;
    // eslint-disable-next-line no-control-regex
    }).replace(/[\u0001-\u0003]/g, '');
    text = decodeEntities(text.replace(/<[^>]+>/g, ''))
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n');
    return text.replace(/^\n+/, '').replace(/\s+$/, '');
}

/** A typed plain-text body as HTML (one <br> per line), safe to send or show. */
export function plainTextToEmailHtml(text) {
    const value = String(text ?? '');
    if (!value) return '';
    return value.split(/\r\n?|\n/).map((line) => escapeHtml(line)).join('<br>');
}

/** HTML body + signature text, the HTML twin of signatureCore.appendSignature (the signature is escaped, never markup). */
export function appendSignatureHtml(html, signature) {
    const sig = typeof signature === 'string' ? signature : signature?.text || '';
    const body = sanitizeEmailHtml(html).replace(/(?:<br>|\s)+$/g, '');
    if (!sig) return body;
    const sigHtml = plainTextToEmailHtml(sig.replace(/^\n+/, ''));
    return body ? `${body}<br><br>${sigHtml}` : sigHtml;
}

// ------------------------------------------------------------------ Cc / Bcc ------------------------------------------

const EMAIL_PATTERN = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]+$/;
export const MAX_EXTRA_RECIPIENTS = 20;

export function isValidEmailAddress(value) {
    return EMAIL_PATTERN.test(String(value ?? '').trim());
}

/**
 * @param {string | string[]} value
 * The addresses typed into Cc or Bcc: comma, semicolon, space or new line between them; "Name <a@b.com>" keeps only the address.
 * Lower-cased, de-duplicated, in typed order. Invalid entries are returned separately so the field can name them.
 */
export function parseEmailAddressList(value) {
    const raw = Array.isArray(value) ? value.join(',') : String(value ?? '');
    const valid = [];
    const invalid = [];
    const seen = new Set();
    raw.split(/[,;\n]+/).flatMap((part) => {
        const angle = /<([^>]+)>/.exec(part);
        return angle ? [angle[1]] : part.split(/\s+/);
    }).map((part) => part.trim().replace(/^mailto:/i, '')).filter(Boolean).forEach((part) => {
        if (!isValidEmailAddress(part)) {
            invalid.push(part);
            return;
        }
        const address = part.toLowerCase();
        if (seen.has(address)) return;
        seen.add(address);
        valid.push(address);
    });
    return { valid, invalid };
}

/**
 * Plain-English problem with one Cc/Bcc field, or '' when it is fine.
 * @param {string | string[]} value
 * @param {string} [label]
 */
export function emailAddressListError(value, label = 'Cc') {
    const { valid, invalid } = parseEmailAddressList(value);
    if (invalid.length) return `${label}: "${invalid[0]}" is not an email address.`;
    if (valid.length > MAX_EXTRA_RECIPIENTS) return `${label}: at most ${MAX_EXTRA_RECIPIENTS} addresses.`;
    return '';
}

/**
 * Cc and Bcc for one send, with the To address and any repeats removed (a person gets one copy).
 * @param {{ to?: string, cc?: string | string[], bcc?: string | string[] }} [recipients]
 * @returns {{ cc: string[], bcc: string[] }}
 */
export function extraEmailRecipients({ to = '', cc = '', bcc = '' } = {}) {
    const toAddress = String(to ?? '').trim().toLowerCase();
    const ccList = parseEmailAddressList(cc).valid.filter((address) => address !== toAddress);
    const bccList = parseEmailAddressList(bcc).valid.filter((address) => address !== toAddress && !ccList.includes(address));
    return { cc: ccList.slice(0, MAX_EXTRA_RECIPIENTS), bcc: bccList.slice(0, MAX_EXTRA_RECIPIENTS) };
}

/** The addresses in an email header value ("A <a@x.com>, b@y.com"), lower-cased. For inbound To / Cc. */
export function addressesFromHeader(value) {
    if (Array.isArray(value)) {
        return value.flatMap((item) => addressesFromHeader(typeof item === 'string' ? item : item?.emailAddress?.address || item?.address || ''));
    }
    const text = String(value ?? '');
    if (!text.trim()) return [];
    const found = text.match(/[^\s<>(),;:"[\]]+@[^\s<>(),;:"[\]]+\.[^\s<>(),;:"[\]]+/g) || [];
    return [...new Set(found.map((address) => address.toLowerCase()))];
}
